import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  ArrowRight, ArrowLeft, Shuffle, ClipboardList, Users, BarChart3,
  Database, Clock3, CheckCircle2, TrendingUp,
  Gauge, DollarSign, Smile, Sparkles,
  Box, Puzzle, Maximize2, ShieldCheck, UsersRound, Award,
} from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import DemoDialog from "@/components/site/DemoDialog";
import AmbientParticles from "@/components/site/AmbientParticles";
import RelatedSolutions from "@/components/site/RelatedSolutions";
import IllustrativePill from "@/components/site/IllustrativePill";
import StandardCTA from "@/components/site/StandardCTA";
import { ENTERPRISES_IMAGES } from "@/lib/site";

const CHALLENGES = [
  { icon: Shuffle,        title: "Siloed systems",      desc: "Critical data lives in silos across departments, tools, and regions." },
  { icon: ClipboardList,  title: "Complex processes",   desc: "Manual workflows and legacy processes slow down execution." },
  { icon: Users,          title: "Low visibility",      desc: "Leaders lack real-time visibility into operations and performance." },
  { icon: BarChart3,      title: "Scaling challenges",  desc: "Hard to maintain efficiency, quality, and control as the business grows." },
];

const SOLUTIONS = [
  { icon: Database,       title: "Unified enterprise operations",        desc: "All data, systems, and teams connected in one intelligent platform." },
  { icon: Clock3,         title: "Real-time intelligence & automation",  desc: "Automate workflows and surface insights when you need them." },
  { icon: CheckCircle2,   title: "Performance at scale",                  desc: "Drive consistency, efficiency, and accountability across the organization." },
  { icon: TrendingUp,     title: "Data-driven decisions",                 desc: "Make faster, smarter decisions with trusted, real-time data." },
];

const RESULTS = [
  { icon: Gauge,      title: "Higher operational efficiency", desc: "Streamline operations and eliminate manual inefficiencies." },
  { icon: DollarSign, title: "Lower operating costs",         desc: "Reduce waste and optimize resource allocation." },
  { icon: Smile,      title: "Improved performance visibility", desc: "Gain real-time visibility across all functions and regions." },
  { icon: Sparkles,   title: "Accelerated business growth",   desc: "Make data-driven decisions that drive growth." },
];

const WHY = [
  { icon: Box,         title: "Enterprise-grade platform",   desc: "Built to handle complexity and scale across global operations." },
  { icon: Puzzle,      title: "Seamless integration",        desc: "Connects with your existing systems and tools." },
  { icon: Maximize2,   title: "Scales with your business",   desc: "From one department to the entire enterprise — we grow with you." },
  { icon: ShieldCheck, title: "Secure & compliant",          desc: "Enterprise-grade security and compliance you can count on." },
  { icon: UsersRound,  title: "Built for every team",        desc: "Empowers every team with the insights and tools they need." },
  { icon: Award,       title: "Built for industry leaders", desc: "Purpose-built for enterprises that demand smarter, faster, more accountable operations." },
];

function SectionTitle({ children, subtitle, testid }) {
  return (
    <div className="text-center fade-up">
      <h2 data-testid={testid}
        className="font-display text-platinum text-[30px] sm:text-[36px] lg:text-[44px] leading-tight tracking-[-0.018em]">
        {children}
      </h2>
      {subtitle && (
        <p className="mt-4 text-[14px] sm:text-[15px] text-platinum/65 leading-[1.7] max-w-[720px] mx-auto">
          {subtitle}
        </p>
      )}
    </div>
  );
}

function CardIcon({ Icon, tone = "sapphire" }) {
  const isGold = tone === "gold";
  const ring  = isGold ? "rgba(217,165,79,0.55)" : "rgba(96,165,250,0.55)";
  const glow  = isGold ? "rgba(217,165,79,0.42)" : "rgba(96,165,250,0.45)";
  const fill  = isGold ? "rgba(217,165,79,0.10)" : "rgba(96,165,250,0.10)";
  const color = isGold ? "#E5C77F" : "#60A5FA";
  return (
    <div className="w-12 h-12 rounded-full inline-flex items-center justify-center shrink-0"
      style={{
        background: `radial-gradient(circle at 30% 30%, ${fill}, #0E1422 75%)`,
        border: `1px solid ${ring}`,
        boxShadow: `0 0 18px -3px ${glow}, inset 0 0 10px ${fill}`,
      }}>
      <Icon className="w-5 h-5" style={{ color }} />
    </div>
  );
}

export default function EnterprisesPage() {
  const navigate = useNavigate();
  const [demoOpen, setDemoOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, []);
  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setScrollY(window.scrollY));
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { window.removeEventListener("scroll", onScroll); cancelAnimationFrame(raf); };
  }, []);

  const heroParallax = `translate3d(0, ${-scrollY * 0.14}px, 0)`;

  return (
    <div className="min-h-screen bg-obsidian text-platinum relative overflow-x-hidden" data-testid="enterprises-page">
      <div className="fixed inset-0 pointer-events-none z-0" aria-hidden>
        <AmbientParticles count={32} tone="mixed" density={0.55} />
      </div>
      <Navbar onBookDemo={() => setDemoOpen(true)} />

      {/* HERO */}
      <section className="relative pt-[120px] lg:pt-[150px] pb-16 lg:pb-24 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none bg-grid opacity-[0.06]" aria-hidden />
        <div className="absolute -top-32 right-0 w-[820px] h-[820px] pointer-events-none"
          style={{ background: "radial-gradient(closest-side, rgba(96,165,250,0.20), transparent)" }} aria-hidden />
        <div className="relative mx-auto max-w-[1440px] px-6 lg:px-10">
          <div className="grid lg:grid-cols-[1fr_1.3fr] gap-8 lg:gap-12 items-center min-h-[560px] lg:min-h-[660px]">
            <div className="fade-up relative z-10">
              <Link to="/solutions" data-testid="enterprises-back"
                className="inline-flex items-center gap-2 text-[12px] text-platinum/60 hover:text-platinum mb-6 transition-colors">
                <ArrowLeft className="w-3.5 h-3.5" /> Back to Solutions
              </Link>
              <div className="text-[10.5px] tracking-[0.38em] uppercase font-medium">
                <span className="text-sapphire-glow">SOLUTIONS</span>
                <span className="text-platinum/40 mx-2">/</span>
                <span className="text-sapphire-glow">ENTERPRISES</span>
              </div>
              <h1 className="font-display mt-5 text-platinum text-[44px] sm:text-[54px] lg:text-[60px] xl:text-[64px] leading-[1.04] tracking-[-0.022em]">
                Operational Intelligence<br />
                <span style={{ textShadow: "0 0 30px rgba(96,165,250,0.25)" }}>For Enterprises</span>
              </h1>
              <p className="mt-6 text-[16px] sm:text-[17px] text-platinum/72 leading-[1.7] max-w-[520px]">
                Smarter operations. Stronger performance.<br />
                Intelligence that scales with your business.
              </p>
              <div className="mt-9 flex flex-wrap items-center gap-3">
                <button onClick={() => navigate("/get-your-audit-report")} data-testid="enterprises-hero-audit"
                  className="btn-primary rounded-md h-12 px-6 text-[14px] font-medium inline-flex items-center gap-2">
                  Get Your Audit Report <ArrowRight className="w-4 h-4" />
                </button>
                <button onClick={() => setDemoOpen(true)} data-testid="enterprises-hero-demo"
                  className="btn-ghost rounded-md h-12 px-6 text-[14px] font-medium inline-flex items-center gap-2">
                  Book a Demo <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Financial-district skyline — homepage-style full-bleed */}
            <div className="relative lg:-mr-10 xl:-mr-20" data-testid="enterprises-hero-visual"
              style={{ transform: heroParallax, willChange: "transform" }}>
              <div className="absolute inset-0 pointer-events-none"
                style={{ background: "radial-gradient(60% 55% at 55% 50%, rgba(37,99,235,0.32), transparent 72%)", filter: "blur(48px)" }} aria-hidden />

              <div className="absolute top-[20%] left-[28%] w-1 h-1 rounded-full bg-sapphire-glow twinkle" style={{ animationDelay: "0.3s", boxShadow: "0 0 10px rgba(96,165,250,1)" }} aria-hidden />
              <div className="absolute top-[34%] right-[22%] w-1.5 h-1.5 rounded-full bg-white twinkle" style={{ animationDelay: "1.4s", boxShadow: "0 0 12px rgba(255,255,255,0.95)" }} aria-hidden />
              <div className="absolute bottom-[36%] left-[16%] w-1 h-1 rounded-full bg-sapphire-glow twinkle" style={{ animationDelay: "2.0s", boxShadow: "0 0 10px rgba(96,165,250,1)" }} aria-hidden />
              <div className="absolute top-[16%] right-[38%] w-[5px] h-[5px] rounded-full bg-sapphire-glow twinkle" style={{ animationDelay: "0.7s", boxShadow: "0 0 14px rgba(96,165,250,1)" }} aria-hidden />
              <div className="absolute bottom-[44%] right-[12%] w-1 h-1 rounded-full bg-amber-200 twinkle" style={{ animationDelay: "1.6s", boxShadow: "0 0 10px rgba(252,211,77,0.9)" }} aria-hidden />

              <div className="relative">
                <img src={ENTERPRISES_IMAGES.hero} alt="Esthera RealityOS — connected operational intelligence across the enterprise financial-district skyline"
                  className="block w-full h-[520px] lg:h-[680px] object-cover object-center"
                  style={{
                    WebkitMaskImage: "radial-gradient(120% 110% at 50% 50%, #000 60%, rgba(0,0,0,0.6) 80%, transparent 100%)",
                    maskImage: "radial-gradient(120% 110% at 50% 50%, #000 60%, rgba(0,0,0,0.6) 80%, transparent 100%)",
                  }}
                  draggable="false" />
                <div className="absolute inset-0 pointer-events-none"
                  style={{ background: "linear-gradient(180deg, rgba(10,15,26,0.75) 0%, rgba(10,15,26,0) 18%, rgba(10,15,26,0) 70%, rgba(10,15,26,0.98) 100%)" }} aria-hidden />
                <div className="absolute inset-0 pointer-events-none"
                  style={{ background: "linear-gradient(90deg, rgba(10,15,26,1) 0%, rgba(10,15,26,0.82) 14%, rgba(10,15,26,0.18) 32%, rgba(10,15,26,0) 50%, rgba(10,15,26,0) 78%, rgba(10,15,26,0.55) 100%)" }} aria-hidden />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* THE CHALLENGE TODAY */}
      <section className="relative pb-16 lg:pb-20">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
          <SectionTitle testid="enterprises-challenge-title"
            subtitle="Enterprises face complexity, disconnected systems, and constant pressure to perform.">
            The Challenge Today
          </SectionTitle>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
            {CHALLENGES.map((c, i) => (
              <div key={c.title} data-testid={`enterprises-challenge-${i}`}
                className="relative rounded-2xl border border-white/[0.07] p-6 lg:p-7 fade-up hover:border-sapphire-glow/35 transition-all duration-500 min-h-[230px]"
                style={{ background: "linear-gradient(180deg, rgba(15,22,40,0.45) 0%, rgba(10,15,26,0.85) 100%)", animationDelay: `${i * 80}ms` }}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 0 30px -8px rgba(96,165,250,0.35)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; }}>
                <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-sapphire-glow/35 to-transparent" />
                <CardIcon Icon={c.icon} tone="sapphire" />
                <div className="mt-5 font-display text-platinum text-[16px] sm:text-[17px] leading-[1.25] tracking-tight">{c.title}</div>
                <p className="mt-2.5 text-[12.5px] sm:text-[13px] text-platinum/60 leading-[1.65]">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW ESTHERA SOLVES IT */}
      <section className="relative pb-16 lg:pb-20">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
          <SectionTitle testid="enterprises-solves-title"
            subtitle="EstheraRealityOS unifies your data, systems, and teams — empowering you to operate with clarity, speed, and confidence.">
            How Esthera Solves It
          </SectionTitle>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
            {SOLUTIONS.map((s, i) => (
              <div key={s.title} data-testid={`enterprises-solution-${i}`}
                className="relative rounded-2xl border border-white/[0.07] p-6 lg:p-7 fade-up hover:border-amber-300/30 transition-all duration-500 min-h-[230px]"
                style={{ background: "linear-gradient(180deg, rgba(20,18,12,0.45) 0%, rgba(10,15,26,0.85) 100%)", animationDelay: `${i * 80}ms` }}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 0 30px -8px rgba(217,165,79,0.28)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; }}>
                <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-amber-300/35 to-transparent" />
                <CardIcon Icon={s.icon} tone="gold" />
                <div className="mt-5 font-display text-platinum text-[16px] sm:text-[17px] leading-[1.25] tracking-tight">{s.title}</div>
                <p className="mt-2.5 text-[12.5px] sm:text-[13px] text-platinum/60 leading-[1.65]">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* REAL RESULTS */}
      <section className="relative pb-16 lg:pb-20">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
          <SectionTitle testid="enterprises-results-title"
            subtitle="Operational intelligence that drives efficiency, reduces costs, and accelerates growth.">
            Real Results for Enterprises
          </SectionTitle>
          <div className="mt-10 relative rounded-2xl border border-white/[0.07] p-6 sm:p-10 lg:p-12 overflow-hidden"
            style={{ background: "linear-gradient(180deg, rgba(13,18,32,0.6) 0%, rgba(10,15,26,0.92) 100%)" }}>
            <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-sapphire-glow/40 to-transparent" />
            <div className="grid lg:grid-cols-[1.15fr_1fr] gap-10 lg:gap-12 items-center">
              {/* Operations dashboard — KEEP all KPI labels readable */}
              <div className="relative aspect-square fade-up">
                <IllustrativePill position="top-3 right-3" z="z-20" />
                <div className="absolute inset-[-6%] pointer-events-none"
                  style={{ background: "radial-gradient(closest-side, rgba(37,99,235,0.30), transparent 72%)", filter: "blur(40px)" }} />
                <img src={ENTERPRISES_IMAGES.results}
                  alt="Enterprise Operations Dashboard (illustrative sample data) — Operational Efficiency 87%, Process Automation 76%, Cost Optimization 24%, Business Agility 92%; Global Operations across 23 active regions and 128 operations; Executive Overview with Revenue Impact +$42.8M, Cost Savings +$18.7M, Efficiency Gain +27%; 8 Business Units; System Health 98%; Key Insights and Performance Trends."
                  className="relative w-full h-full object-contain breathe-glow"
                  style={{
                    mixBlendMode: "screen",
                    filter: "brightness(0.85) contrast(2.8) saturate(1.20)",
                    WebkitMaskImage: "radial-gradient(ellipse 96% 94% at 50% 50%, #000 60%, rgba(0,0,0,0.5) 82%, transparent 100%)",
                    maskImage: "radial-gradient(ellipse 96% 94% at 50% 50%, #000 60%, rgba(0,0,0,0.5) 82%, transparent 100%)",
                  }}
                  draggable="false" />
              </div>

              {/* 2x2 outcome grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-8 lg:gap-x-10 lg:gap-y-10">
                {RESULTS.map((r, i) => (
                  <div key={r.title} data-testid={`enterprises-result-${i}`}
                    className="fade-up" style={{ animationDelay: `${i * 80}ms` }}>
                    <CardIcon Icon={r.icon} tone="sapphire" />
                    <div className="mt-4 font-display text-platinum text-[16px] sm:text-[17px] leading-[1.25]">{r.title}</div>
                    <p className="mt-2 text-[12.5px] sm:text-[13px] text-platinum/60 leading-[1.65] max-w-[260px]">{r.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY ESTHERA FOR ENTERPRISES — 6 cards in 3-col grid (2 rows) */}
      <section className="relative pb-16 lg:pb-20">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
          <SectionTitle testid="enterprises-why-title"
            subtitle="Built for forward-thinking enterprises who demand clarity, control, and performance.">
            Why Esthera for Enterprises
          </SectionTitle>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
            {WHY.map((w, i) => (
              <div key={w.title} data-testid={`enterprises-why-${i}`}
                className="relative rounded-2xl border border-white/[0.07] p-6 lg:p-7 fade-up hover:border-sapphire-glow/35 transition-all duration-500 min-h-[190px]"
                style={{ background: "linear-gradient(180deg, rgba(15,22,40,0.45) 0%, rgba(10,15,26,0.85) 100%)", animationDelay: `${i * 70}ms` }}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 0 30px -8px rgba(96,165,250,0.35)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; }}>
                <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-sapphire-glow/35 to-transparent" />
                <div className="flex items-start gap-4">
                  <CardIcon Icon={w.icon} tone="sapphire" />
                  <div className="flex-1">
                    <div className="font-display text-platinum text-[16px] sm:text-[17px] leading-[1.25] tracking-tight">{w.title}</div>
                    <p className="mt-2.5 text-[12.5px] sm:text-[13px] text-platinum/60 leading-[1.65]">{w.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AUDIT CTA — uses StandardCTA (uniform site-wide) */}
      <StandardCTA
        testId="enterprises-cta"
        eyebrow="GET STARTED"
        eyebrowTone="positioning"
        title="See exactly where your operations can improve."
        highlight="can improve."
        subtitle="Start with a free operational intelligence audit — no cost, no commitment."
        crystal={ENTERPRISES_IMAGES.auditCta}
        primaryLabel="Get Your Audit Report"
        primaryHref="/get-your-audit-report"
        secondaryLabel="Book a Demo"
        secondaryHref="/book-demo"
      />

      <RelatedSolutions currentSlug="enterprises" />
      <Footer />
      <DemoDialog open={demoOpen} onClose={() => setDemoOpen(false)} />
    </div>
  );
}
